import { Anchor, Box, Button, Modal, Skeleton, Table, Text, Textarea } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { CalendarPlusIcon, CheckIcon, FilePdfIcon, FileTextIcon, PaperPlaneTiltIcon, XIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import useSWR, { mutate as globalMutate } from "swr";
import { ReconciliationApi, SettlementApi } from "../../apis";
import { ExtendPaymentModal } from "../../components/invoice-actions/extend-payment-modal";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import { Page, PageHeader } from "../../components/layout/Page";
import { ListToolbar, RowActions } from "../../components/list";
import { InvoicePeekDrawer, peekKey } from "../../components/settlement/invoice-peek";
import { ModalFooter } from "../../components/ui/app-modal";
import modalChrome from "../../components/ui/app-modal.module.css";
import grid from "../../components/ui/data-grid.module.css";
import { Notice } from "../../components/ui/notice";
import { FlowStrip } from "../../components/ui/flow-strip";
import { StatusBadge } from "../../components/ui/status-badge";
import { usePermission } from "../../components/permission";
import { usePdfDownload } from "../../hooks/usePdfDownload";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { ubPeriod } from "../../utils/reconciliation-period";
import { ubFormat } from "../../utils/ub-time";
import base from "../account-balance/SettlementDetails.module.css";
import classes from "./StatementDetails.module.css";
import { ReasonTooltip } from "../../components/ui/reason-tooltip";

const STATUS_COLORS: Record<string, string> = {
  DRAFT: "gray",
  SENT: "gray",
  VIEWED: "gray",
  APPROVED: "green",
  REJECTED: "red",
  REVISED: "yellow",
  VOID: "dark",
};

// Хуулгын мөрийн төрөл (backend: logics/statement/engine.ts). Хуучин хадгалсан мөр `type`-тай, `kind`-гүй.
const KIND_COLORS: Record<string, string> = {
  INVOICE: "gray",
  PAYMENT: "green",
  RETURN: "yellow",
  CREDIT_NOTE: "yellow",
  CANCELLED: "red",
  AMENDMENT: "yellow",
  DEBIT_NOTE: "gray",
  LATE_FEE: "gray",
};

// Табд орох мөрийн төрлүүд.
const TAB_KINDS: Record<string, string[]> = {
  invoice: ["INVOICE"],
  payment: ["PAYMENT"],
  returned: ["RETURN", "CREDIT_NOTE"],
  cancelled: ["CANCELLED"],
  amendment: ["AMENDMENT"],
};
// Тоо нь 0 үед нуугдах табууд (үргэлж харагдах: all, invoice, payment, overdue).
const OPTIONAL_TABS = ["returned", "cancelled", "amendment"];
const TABS = ["all", "invoice", "payment", "returned", "cancelled", "amendment", "overdue"] as const;
type Tab = (typeof TABS)[number];
const COLUMN_COUNT = 9;

type StatementRow = {
  key: string;
  kind: string;
  at: string;
  refCode: string;
  invoiceId: string | null;
  direction: "ADD" | "SUB";
  amount: number;
  balance: number;
};

type InvoiceState = {
  dueDay: string | null;
  overdueDays: number | null;
  paymentStatus: string;
  invoiceStatus: string;
  amountToPay: number;
  confirmedAmount: number;
};

/** Шинэ (kind-тэй) ба хуучин (type, debit/credit, runningBalance) мөрийг нэг хэлбэрт оруулна. */
const normalizeRow = (tx: any, i: number): StatementRow => {
  const debit = Number(tx.debit) || 0;
  const credit = Number(tx.credit) || 0;
  const direction: "ADD" | "SUB" = tx.direction ?? (debit > 0 ? "ADD" : "SUB");
  return {
    key: `${tx.invoiceId ?? tx.referenceNumber ?? "row"}-${tx.kind ?? tx.type}-${i}`,
    kind: tx.kind ?? tx.type ?? "",
    at: tx.date,
    refCode: tx.referenceNumber ?? "",
    invoiceId: tx.invoiceId ?? null,
    direction,
    amount: Number(tx.amount ?? (debit || credit)) || 0,
    balance: Number(tx.balanceAfter ?? tx.runningBalance) || 0,
  };
};

const initials = (name: string) => Array.from(name.trim()).slice(0, 2).join("").toUpperCase() || "-";

// Хоёр огнын UB календарийн өдрийн зөрүү (09-20 18:00 → 10-02 = 12).
const ubDaysSince = (value: string) =>
  dayjs().utcOffset(480).startOf("day").diff(dayjs(value).utcOffset(480).startOf("day"), "day");

export const StatementDetails = () => {
  const { t } = useTranslation(["reconciliation", "common", "invoice"]);
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openInvoiceId = searchParams.get("inv");
  const { user } = useSelector((state: { auth: IAuth }) => state.auth);

  const [tab, setTab] = useState<Tab>("all");
  const [rejectModalOpened, setRejectModalOpened] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [buyerNotes, setBuyerNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [extendId, setExtendId] = useState("");
  const [extendOpened, extendHandlers] = useDisclosure(false);

  // backend-тэй ижил: харах = INV_SETTLEMENT_GET, илгээх/засах = INV_SETT_SEND, батлах/татгалзах = INV_SETTLEMENT_ACCEPT_PAYMENT.
  const INV_SETTLEMENT_GET = usePermission("INV_SETTLEMENT_GET");
  const INV_SETT_SEND = usePermission("INV_SETT_SEND");
  const INV_SETTLEMENT_ACCEPT_PAYMENT = usePermission("INV_SETTLEMENT_ACCEPT_PAYMENT");
  const INV_SETT_RES = usePermission("INV_SETT_RES");
  const canRespond = INV_SETTLEMENT_ACCEPT_PAYMENT.hasAccess || INV_SETT_RES.hasAccess;

  const { data, error, isLoading, mutate } = useSWR(id && INV_SETTLEMENT_GET.hasAccess ? `swr.statement.detail.${id}` : null, () => ReconciliationApi.get(id!), {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });
  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: ReconciliationApi.pdf });

  // Хуучин «Тооцоо нийлэх» холбоос (/settlement/<хуучин settlement id>) нь тооцооны хуудас биш:
  // олдохгүй бол хуучин мөр мөн эсэхийг шалгаад зөвхөн унших дэлгэц рүү шилжүүлнэ.
  // Шалгалт бүтэлгүйтвэл («хуучин мөр ч биш», эрхгүй) skeleton-д гацалгүй «олдсонгүй» харуулна.
  const [notLegacy, setNotLegacy] = useState(false);
  useEffect(() => {
    if (!error || !id) return;
    let cancelled = false;
    SettlementApi.get(id)
      .then(() => {
        if (!cancelled) navigate(`/settlement/legacy/${id}`, { replace: true });
      })
      .catch(() => {
        if (!cancelled) setNotLegacy(true);
      });
    return () => {
      cancelled = true;
    };
  }, [error, id, navigate]);

  const currentBusinessId = user?.currentBusiness?.id;
  const isSeller = data?.sellerBusinessId === currentBusinessId;
  const isBuyer = data?.buyerBusinessId === currentBusinessId;
  const status: string | undefined = data?.status;
  const awaitingBuyer = status === "SENT" || status === "VIEWED";
  const dash = t("common:list.dash");
  const invoiceStates: Record<string, InvoiceState> = useMemo(() => data?.invoiceStates ?? {}, [data]);

  const allRows: StatementRow[] = useMemo(
    () => (Array.isArray(data?.transactions) ? data.transactions.map(normalizeRow) : []),
    [data],
  );
  const isOverdue = useCallback(
    (r: StatementRow) => r.kind === "INVOICE" && !!r.invoiceId && (invoiceStates[r.invoiceId]?.overdueDays ?? 0) > 0,
    [invoiceStates],
  );
  const counts = useMemo(() => {
    const byKinds = (kinds: string[]) => allRows.filter((r) => kinds.includes(r.kind)).length;
    return {
      all: allRows.length,
      invoice: byKinds(TAB_KINDS.invoice),
      payment: byKinds(TAB_KINDS.payment),
      returned: byKinds(TAB_KINDS.returned),
      cancelled: byKinds(TAB_KINDS.cancelled),
      amendment: byKinds(TAB_KINDS.amendment),
      overdue: allRows.filter(isOverdue).length,
    } as Record<Tab, number>;
  }, [allRows, isOverdue]);
  const rows = useMemo(() => {
    if (tab === "all") return allRows;
    if (tab === "overdue") return allRows.filter(isOverdue);
    return allRows.filter((r) => TAB_KINDS[tab].includes(r.kind));
  }, [allRows, tab, isOverdue]);
  const sums = useMemo(
    () => ({
      add: rows.filter((r) => r.direction === "ADD").reduce((s, r) => s + r.amount, 0),
      sub: rows.filter((r) => r.direction === "SUB").reduce((s, r) => s + r.amount, 0),
    }),
    [rows],
  );

  // Идэвхтэй таб өгөгдөл шинэчлэгдсэний дараа хоосорвол (нуугдсан таб) «Бүгд» рүү буцна.
  useEffect(() => {
    if (OPTIONAL_TABS.includes(tab) && counts[tab] === 0) setTab("all");
  }, [tab, counts]);

  const totals = data?.totals ?? {};
  const money = (v?: number | string | null) => (v == null || v === "" ? dash : currencyFormat(Number(v), { withSymbol: false }));
  const period = ubPeriod(data?.periodFrom, data?.periodTo);
  const periodStart = period?.from;
  const periodEnd = period?.to;

  const run = async (fn: () => Promise<unknown>, success: string, failure: string, after?: () => void) => {
    setActionLoading(true);
    try {
      await fn();
      message.success(success);
      after?.();
      mutate();
    } catch (e) {
      handleError(e, failure);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSend = () => {
    if (!window.confirm(t("reconciliation:confirm.send"))) return;
    void run(() => ReconciliationApi.send(id!), t("reconciliation:success.sent"), t("reconciliation:error.send"));
  };

  const handleApprove = () => {
    if (!window.confirm(t("reconciliation:confirm.approve"))) return;
    void run(
      () => ReconciliationApi.approve(id!, { buyerNotes: buyerNotes.trim() || undefined }),
      t("reconciliation:success.approved"),
      t("reconciliation:error.approve"),
    );
  };

  const handleReject = () => {
    if (!rejectionReason.trim()) {
      message.error(t("reconciliation:error.noReason"));
      return;
    }
    void run(
      () => ReconciliationApi.reject(id!, { rejectionReason: rejectionReason.trim() }),
      t("reconciliation:success.rejected"),
      t("reconciliation:error.reject"),
      () => {
        setRejectModalOpened(false);
        setRejectionReason("");
      },
    );
  };

  const handleRevise = () => {
    if (!window.confirm(t("reconciliation:confirm.revise"))) return;
    void run(() => ReconciliationApi.revise(id!), t("reconciliation:success.revise"), t("reconciliation:error.revise"));
  };

  const openInvoice = (invoiceId: string) => {
    const p = new URLSearchParams(searchParams);
    p.set("inv", invoiceId);
    setSearchParams(p);
  };
  const closeInvoice = () => {
    const p = new URLSearchParams(searchParams);
    p.delete("inv");
    setSearchParams(p, { replace: true });
  };
  const openExtend = (invoiceId: string) => {
    setExtendId(invoiceId);
    extendHandlers.open();
  };
  const onExtended = () => {
    void mutate();
    void globalMutate(peekKey(extendId));
  };

  const breadcrumb = (
    <Breadcrumb
      data={[
        { label: t("reconciliation:breadcrumb.list"), to: "/settlement" },
        { label: data?.statementNumber ?? dash },
      ]}
    />
  );

  if (!INV_SETTLEMENT_GET.hasAccess) {
    return (
      <Page>
        <PageHeader breadcrumb={breadcrumb} title={t("reconciliation:detail.titlePrefix")} />
        {INV_SETTLEMENT_GET.accessDenied()}
      </Page>
    );
  }

  if (isLoading || !data) {
    return (
      <Page>
        <PageHeader breadcrumb={breadcrumb} title={t("reconciliation:detail.titlePrefix")} />
        {isLoading || (error && !notLegacy) ? <Skeleton h={120} radius="lg" /> : <Text c="dimmed">{t("common:notFound")}</Text>}
      </Page>
    );
  }

  // Явц: Ноорог → Илгээсэн → Харсан → Баталсан/Татгалзсан.
  const currentStep = status === "APPROVED" || status === "REJECTED" ? 4 : status === "VIEWED" ? 2 : status === "SENT" ? 1 : 0;
  const finalRejected = status === "REJECTED";
  const steps = [
    { title: t("reconciliation:detail.step.draft"), sub: t("reconciliation:detail.step.created") },
    { title: t("reconciliation:detail.step.sent"), sub: data.sentAt ? ubFormat(data.sentAt, "MM-DD HH:mm") : dash },
    {
      title: t("reconciliation:detail.step.viewed"),
      sub: data.viewedAt ? ubFormat(data.viewedAt, "MM-DD HH:mm") : t("reconciliation:detail.step.notViewed"),
    },
    {
      title: finalRejected ? t("reconciliation:detail.step.rejected") : t("reconciliation:detail.step.approved"),
      sub: finalRejected && data.rejectedAt
        ? ubFormat(data.rejectedAt, "MM-DD HH:mm")
        : data.approvedAt
          ? ubFormat(data.approvedAt, "MM-DD HH:mm")
          : t("reconciliation:detail.step.pending"),
    },
  ];
  const stepState = (i: number) => (i < currentStep ? "done" : i === currentStep ? "now" : "todo");

  const amendments = Number(totals.totalAmendments) || 0;
  const cancelled = Number(totals.totalCancelled) || 0;
  const returned = (Number(totals.totalReturns) || 0) + (Number(totals.totalCreditNotes) || 0);
  const extra = (Number(totals.totalDebitNotes) || 0) + (Number(totals.totalLateFees) || 0);
  const flow = [
    { label: t("reconciliation:detail.flow.opening"), value: money(totals.openingBalance), hint: t("reconciliation:detail.flow.asOf", { date: periodStart ?? dash }) },
    {
      label: `+ ${t("reconciliation:detail.flow.invoiced")}`,
      value: money(totals.totalInvoiced),
      hint: t("reconciliation:detail.flow.invoiceCount", { count: counts.invoice }),
    },
    ...(amendments !== 0
      ? [{
          label: `${amendments > 0 ? "+" : "−"} ${t("reconciliation:detail.flow.amendments")}`,
          value: money(Math.abs(amendments)),
          hint: t("reconciliation:detail.flow.amendmentsHint"),
        }]
      : []),
    ...(extra > 0
      ? [{ label: `+ ${t("reconciliation:detail.flow.extra")}`, value: money(extra), hint: t("reconciliation:detail.flow.extraHint") }]
      : []),
    {
      label: `− ${t("reconciliation:detail.flow.paid")}`,
      value: money(totals.totalPayments),
      hint: Number(totals.totalPayments) ? t("reconciliation:detail.flow.paymentCount", { count: counts.payment }) : t("reconciliation:detail.flow.noPayments"),
      muted: !Number(totals.totalPayments),
    },
    {
      label: `− ${t("reconciliation:detail.flow.returned")}`,
      value: money(returned),
      hint: returned ? t("reconciliation:detail.flow.returnedHint") : t("reconciliation:detail.flow.noReturns"),
      muted: !returned,
    },
    ...(cancelled > 0
      ? [{ label: `− ${t("reconciliation:detail.flow.cancelled")}`, value: money(cancelled), hint: t("reconciliation:detail.flow.cancelledHint", { count: counts.cancelled }) }]
      : []),
    {
      label: `= ${t("reconciliation:detail.flow.closing")}`,
      value: totals.closingBalance != null ? currencyFormat(Number(totals.closingBalance), { withSymbol: true }) : dash,
      hint: t("reconciliation:detail.flow.asOf", { date: periodEnd ?? dash }),
      total: true,
    },
  ];

  const counterpartyName: string = (isSeller ? data.buyerBusinessName : data.sellerBusinessName) ?? dash;
  const myName: string = (isSeller ? data.sellerBusinessName : data.buyerBusinessName) ?? dash;
  const showNotViewed = isSeller && status === "SENT" && !data.viewedAt && !!data.sentAt;
  const showRejected = status === "REJECTED";
  const showLive = isSeller && status === "DRAFT" && !!data.isLive;
  const ownNote: string | null = (isSeller ? data.sellerNotes : data.buyerNotes) || null;
  const otherNote: string | null = (isSeller ? data.buyerNotes : data.sellerNotes) || null;
  const canPdf = !!status && status !== "DRAFT" && status !== "VOID";
  const invoiceLink = (invoiceId: string) => (isSeller ? `/sales-invoice/${invoiceId}/details` : `/buyer-invoice/${invoiceId}/details`);

  const tabs = TABS.filter((v) => !OPTIONAL_TABS.includes(v) || counts[v] > 0).map((v) => ({
    value: v,
    label: t(`reconciliation:detail.tabs.${v}`),
    count: counts[v],
    ...(v === "overdue" ? { tone: "danger" as const } : {}),
  }));

  const invoiceStatusCell = (r: StatementRow) => {
    const st = r.kind === "INVOICE" && r.invoiceId ? invoiceStates[r.invoiceId] : undefined;
    if (!st) return null;
    if (st.invoiceStatus === "CANCELED") {
      return <Text span className={`${base.pill} ${base.pillDanger}`}>{t("reconciliation:detail.inv.cancelled")}</Text>;
    }
    if (st.paymentStatus === "CLOSED" || st.invoiceStatus === "CLOSED") {
      return (
        <Box component="span" className={base.statusCell}>
          <Text span className={`${base.pill} ${base.pillNeutral}`}>{t("reconciliation:detail.inv.closed")}</Text>
          <Text span className={base.statusSub}>{t("reconciliation:detail.inv.fullyPaid")}</Text>
        </Box>
      );
    }
    const days = st.overdueDays;
    const sub = st.paymentStatus === "PENDING" || Number(st.amountToPay) === Number(st.confirmedAmount)
      ? t("reconciliation:detail.inv.unpaid")
      : t("reconciliation:detail.inv.left", { amount: money(st.amountToPay) });
    let pill: { cls: string; text: string } | null = null;
    if (days != null && days > 0) pill = { cls: base.pillDanger, text: t("reconciliation:detail.inv.overdue", { count: days }) };
    else if (days != null && days < 0) pill = { cls: base.pillNeutral, text: t("reconciliation:detail.inv.remaining", { count: -days }) };
    else if (days === 0) pill = { cls: base.pillWarn, text: t("reconciliation:detail.inv.dueToday") };
    if (!pill) return null;
    return (
      <Box component="span" className={base.statusCell}>
        <Text span className={`${base.pill} ${pill.cls}`}>{pill.text}</Text>
        <Text span className={base.statusSub}>{sub}</Text>
      </Box>
    );
  };

  const actions = (
    <Box className={base.headerExtra}>
      {isSeller && (status === "DRAFT" || status === "REVISED") && (
        <ReasonTooltip reason={INV_SETT_SEND.reason}>
          <Button color="brand" loading={actionLoading} onClick={handleSend} leftSection={<PaperPlaneTiltIcon size={16} weight="bold" />}>
            {t("reconciliation:action.send")}
          </Button>
        </ReasonTooltip>
      )}
      {canPdf && (isSeller || isBuyer) && (
        <Button variant="default" loading={isPdfLoading(`${id}`)} onClick={() => downloadPdf(`${id}`)} leftSection={<FilePdfIcon size={16} />}>
          {t("reconciliation:detail.pdf")}
        </Button>
      )}
      {isBuyer && awaitingBuyer && canRespond && (
        <>
          <Button color="red" variant="outline" onClick={() => setRejectModalOpened(true)} leftSection={<XIcon size={16} weight="bold" />}>
            {t("reconciliation:action.reject")}
          </Button>
          <Button color="brand" loading={actionLoading} onClick={handleApprove} leftSection={<CheckIcon size={16} weight="bold" />}>
            {t("reconciliation:action.approve")}
          </Button>
        </>
      )}
      {isSeller && status === "REJECTED" && (
        <ReasonTooltip reason={INV_SETT_SEND.reason}>
          <Button color="brand" loading={actionLoading} onClick={handleRevise} leftSection={<PaperPlaneTiltIcon size={16} weight="bold" />}>
            {t("reconciliation:detail.reviseResend")}
          </Button>
        </ReasonTooltip>
      )}
    </Box>
  );

  const showFraming = tab === "all";

  return (
    <Page>
      <PageHeader
        align="center"
        breadcrumb={breadcrumb}
        title={
          <Box component="span" className={base.titleRow}>
            <Text span inherit>
              {t("reconciliation:detail.titlePrefix")}{" "}
              <Text span inherit className={base.titleMono}>
                {data.statementNumber}
              </Text>
            </Text>
            <StatusBadge color={STATUS_COLORS[status ?? ""]} label={t(`reconciliation:status.${status}`, { defaultValue: status })} />
          </Box>
        }
        extra={actions}
      />

      <Box className={classes.infoRow}>
        <Box className={base.party}>
          {isSeller ? (
            <Text span>{myName}</Text>
          ) : (
            <Text span fw={500} c="gray.9">
              {counterpartyName}
            </Text>
          )}
          <Text span className={base.sep} aria-hidden>
            →
          </Text>
          <Text span className={base.partyAvatar}>
            {initials(isSeller ? counterpartyName : myName)}
          </Text>
          {isSeller ? (
            <Anchor component={Link} to={`/customer-billing/${data.buyerBusinessId}`} className={base.partyName} underline="hover">
              {counterpartyName}
            </Anchor>
          ) : (
            <Text span className={base.partyName}>
              {myName}
            </Text>
          )}
          <Text span className={base.sep} aria-hidden>
            |
          </Text>
          <Text span>{periodStart && periodEnd ? `${periodStart.slice(0, 7)} · ${periodStart.slice(5)} - ${periodEnd.slice(5)}` : dash}</Text>
          <Text span className={base.sep} aria-hidden>
            |
          </Text>
          <Text span>{data.currency ?? "MNT"}</Text>
        </Box>
        <Box component="ol" aria-label={t("reconciliation:detail.progress")} className={base.stepper}>
          {steps.map((s, i) => (
            <Box component="li" key={i} className={base.step}>
              <Box component="span" className={base.stepDot} data-state={stepState(i)} />
              <Box component="span" className={base.stepText}>
                <Text span className={base.stepTitle} data-active={i <= currentStep || undefined}>
                  {s.title}
                </Text>
                <Text span className={base.stepSub}>
                  {s.sub}
                </Text>
              </Box>
              {i < steps.length - 1 && <Box component="span" className={base.stepLine} />}
            </Box>
          ))}
        </Box>
      </Box>

      {showLive && <Notice title={t("reconciliation:detail.alert.draftLive")} />}
      {showNotViewed && (
        <Notice title={t("reconciliation:detail.alert.notViewed", { days: ubDaysSince(data.sentAt) })}>
          {Number(data.remindersSent) > 0 &&
            data.lastReminderAt &&
            t("reconciliation:detail.alert.reminders", {
              count: Number(data.remindersSent),
              last: ubFormat(data.lastReminderAt, "MM-DD HH:mm"),
            })}
        </Notice>
      )}
      {showRejected && (
        <Notice tone="danger" role="alert" title={t("reconciliation:detail.alert.rejected")}>
          {data.rejectionReason || t("reconciliation:detail.alert.noReason")}
        </Notice>
      )}

      <FlowStrip label={t("reconciliation:detail.flow.title")} cells={flow} />

      <Box className={grid.card}>
        <ListToolbar
          tabs={tabs}
          activeTab={tab}
          onTabChange={(v) => setTab(v as Tab)}
          right={
            <Text span className={base.toolbarHint}>
              {t("reconciliation:detail.toolbarHint")}
            </Text>
          }
        />
        <Box className={grid.wrap}>
          <Table className={`${grid.table} ${base.tableMin}`} withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th className={grid.stickyActions} w={96}>
                  <Text className={grid.stickyActionsHead}>{t("reconciliation:col.actions")}</Text>
                </Table.Th>
                <Table.Th>{t("reconciliation:detail.col.date")}</Table.Th>
                <Table.Th>{t("reconciliation:detail.col.invoice")}</Table.Th>
                <Table.Th>{t("reconciliation:detail.col.kind")}</Table.Th>
                <Table.Th className={grid.num}>{t("reconciliation:detail.col.added")}</Table.Th>
                <Table.Th className={grid.num}>{t("reconciliation:detail.col.subtracted")}</Table.Th>
                <Table.Th className={grid.num}>{t("reconciliation:detail.col.balance")}</Table.Th>
                <Table.Th className={base.dueCell}>{t("reconciliation:detail.col.dueDate")}</Table.Th>
                <Table.Th>{t("reconciliation:detail.col.invoiceStatus")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showFraming && (
                <Table.Tr className={grid.subRow}>
                  <Table.Td className={grid.stickyActions} />
                  <Table.Td colSpan={5}>{t("reconciliation:detail.openingRow", { date: periodStart ?? dash })}</Table.Td>
                  <Table.Td className={`${grid.num} ${base.bold}`}>{money(totals.openingBalance)}</Table.Td>
                  <Table.Td colSpan={2} />
                </Table.Tr>
              )}
              {rows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={COLUMN_COUNT} className={grid.empty}>
                    {t("reconciliation:detail.empty")}
                  </Table.Td>
                </Table.Tr>
              ) : (
                rows.map((r) => {
                  const isAdd = r.direction === "ADD";
                  const st = r.invoiceId ? invoiceStates[r.invoiceId] : undefined;
                  const canExtend = isSeller && r.kind === "INVOICE" && (st?.overdueDays ?? 0) > 0;
                  const clickable = !!r.invoiceId;
                  return (
                    <Table.Tr
                      key={r.key}
                      className={clickable ? base.rowClickable : undefined}
                      onClick={clickable ? () => openInvoice(r.invoiceId!) : undefined}
                    >
                      <Table.Td className={grid.stickyActions} onClick={(e) => e.stopPropagation()}>
                        <RowActions
                          inlineOnly
                          items={[
                            {
                              key: "details",
                              intent: "view",
                              icon: <FileTextIcon size={17} />,
                              label: t("reconciliation:tooltip.details"),
                              onClick: () => openInvoice(r.invoiceId!),
                              hidden: !clickable,
                            },
                            {
                              key: "extend",
                              intent: "edit",
                              icon: <CalendarPlusIcon size={17} />,
                              label: t("invoice:extendTooltip"),
                              onClick: () => openExtend(r.invoiceId!),
                              hidden: !canExtend,
                            },
                          ]}
                        />
                      </Table.Td>
                      <Table.Td className={grid.muted}>{ubFormat(r.at, "MM-DD HH:mm")}</Table.Td>
                      <Table.Td>
                        {r.invoiceId ? (
                          <Link to={invoiceLink(r.invoiceId)} onClick={(e) => e.stopPropagation()} className={grid.docLink}>
                            {r.refCode || dash}
                          </Link>
                        ) : (
                          <Text span className={base.mono}>
                            {r.refCode || dash}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <StatusBadge
                          withDot={false}
                          color={KIND_COLORS[r.kind] ?? "gray"}
                          label={r.kind ? t(`reconciliation:detail.kind.${r.kind}`, { defaultValue: t("reconciliation:detail.kind.OTHER") }) : dash}
                        />
                      </Table.Td>
                      <Table.Td className={`${grid.num} ${grid.muted}`}>{isAdd ? money(r.amount) : "-"}</Table.Td>
                      <Table.Td className={`${grid.num} ${grid.muted}`}>{isAdd ? "-" : money(r.amount)}</Table.Td>
                      <Table.Td className={`${grid.num} ${base.bold}`}>{money(r.balance)}</Table.Td>
                      <Table.Td className={`${base.dueCell} ${grid.muted}`}>{r.kind === "INVOICE" ? (st?.dueDay ?? dash) : ""}</Table.Td>
                      <Table.Td>{invoiceStatusCell(r)}</Table.Td>
                    </Table.Tr>
                  );
                })
              )}
            </Table.Tbody>
            {rows.length > 0 && (
              <Table.Tfoot>
                <Table.Tr className={grid.subRow}>
                  <Table.Td className={grid.stickyActions} />
                  <Table.Td colSpan={3}>{t("reconciliation:detail.closingRow", { date: periodEnd ?? dash })}</Table.Td>
                  <Table.Td className={grid.num}>{money(sums.add)}</Table.Td>
                  <Table.Td className={grid.num}>{money(sums.sub)}</Table.Td>
                  <Table.Td className={grid.num}>{currencyFormat(Number(totals.closingBalance), { withSymbol: true })}</Table.Td>
                  <Table.Td colSpan={2} />
                </Table.Tr>
              </Table.Tfoot>
            )}
          </Table>
        </Box>
      </Box>

      <Box className={classes.notes}>
        <Box className={classes.note}>
          <Text span className={classes.noteLabel}>
            {isSeller ? t("reconciliation:detail.notesCard.mine") : t("reconciliation:detail.notesCard.buyerSeesSeller")}
          </Text>
          {isSeller ? (
            ownNote ? (
              <Text span className={classes.noteBody}>{ownNote}</Text>
            ) : (
              <Text span className={classes.noteEmpty}>{t("reconciliation:detail.notesCard.noMine")}</Text>
            )
          ) : data.sellerNotes ? (
            <Text span className={classes.noteBody}>{data.sellerNotes}</Text>
          ) : (
            <Text span className={classes.noteEmpty}>{t("reconciliation:detail.notesCard.noSeller")}</Text>
          )}
        </Box>
        <Box className={classes.note}>
          <Text span className={classes.noteLabel}>
            {isSeller ? t("reconciliation:detail.notesCard.theirs") : t("reconciliation:detail.notesCard.yourReply")}
          </Text>
          {isBuyer && awaitingBuyer && canRespond ? (
            <Textarea
              placeholder={t("reconciliation:notes.buyerNotePlaceholder")}
              value={buyerNotes}
              onChange={(e) => setBuyerNotes(e.currentTarget.value)}
              minRows={2}
              maxRows={4}
              autosize
            />
          ) : isSeller ? (
            data.rejectionReason || otherNote ? (
              <>
                {otherNote && <Text span className={classes.noteBody}>{otherNote}</Text>}
                {data.rejectionReason && <Text span className={classes.noteDanger}>{data.rejectionReason}</Text>}
              </>
            ) : (
              <Text span className={classes.noteEmpty}>{t("reconciliation:detail.notesCard.noReply")}</Text>
            )
          ) : ownNote || data.rejectionReason ? (
            <>
              {ownNote && <Text span className={classes.noteBody}>{ownNote}</Text>}
              {data.rejectionReason && <Text span className={classes.noteDanger}>{data.rejectionReason}</Text>}
            </>
          ) : (
            <Text span className={classes.noteEmpty}>{dash}</Text>
          )}
        </Box>
      </Box>

      <InvoicePeekDrawer invoiceId={openInvoiceId} onClose={closeInvoice} onExtend={(inv) => openExtend(inv.id)} />

      <ExtendPaymentModal invoiceId={extendId} opened={extendOpened} onClose={extendHandlers.close} onSuccess={onExtended} />

      <Modal
        opened={rejectModalOpened}
        onClose={() => setRejectModalOpened(false)}
        title={t("reconciliation:rejectModal.title")}
        size="sm"
        classNames={{ body: modalChrome.modalBodyFlush }}
      >
        <Box px={24} pb={20}>
          <Textarea
            label={t("reconciliation:rejectModal.label")}
            placeholder={t("reconciliation:rejectModal.placeholder")}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
            minRows={3}
            maxRows={6}
            required
            size="xs"
          />
        </Box>
        <ModalFooter>
          <Button variant="default" onClick={() => setRejectModalOpened(false)}>
            {t("common:cancel")}
          </Button>
          <Button color="red" onClick={handleReject} loading={actionLoading}>
            {t("reconciliation:action.reject")}
          </Button>
        </ModalFooter>
      </Modal>
    </Page>
  );
};
